// MF-568 — Card 'pat-class-ranking' (design 1A): one dot per student ranked by PAT scale score over the achievement
// bands, with Score / Stanine / Band columns beside it. Click a band chip or rows to pick a teaching group.
// Rows: { student_id, name, year_level, class_name, score, band, stanine, stanine_source, test_date }.
import { useMemo, useState } from 'react'
import ReactECharts from 'echarts-for-react'
import { Box, Typography } from '@mui/material'
import { SUMMARY_PALETTE, INK, FONT, baseTextStyle, tooltipBase } from '../boardTheme'
import { resolveColors, useBoardStyle } from '../boardPalettes'
import { buttonProps, fmtDay, prepareClass, useChartResize } from './patShared'
import { GroupLine, Interpretation, Roster, StanineNote } from './PatParts'

const ROW = 26
const TOP = 26
const BOTTOM = 34

const PatClassRankingCard = ({ data }) => {
  const style = useBoardStyle()
  const [primary, accent] = resolveColors(style, {}, 2)
  const cls = useMemo(() => prepareClass(data?.rows || []), [data])
  const { students, bands } = cls
  const [picked, setPicked] = useState([])
  const sel = useMemo(() => new Set(picked), [picked])
  const { chartRef, boxRef } = useChartResize()
  const showDates = cls.dates.length > 1

  const toggle = (id) => setPicked((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]))
  const toggleBand = (key) => {
    const ids = students.filter((s) => s.bandKey === key).map((s) => s.id)
    setPicked((p) =>
      ids.every((i) => p.includes(i)) ? p.filter((i) => !ids.includes(i)) : [...new Set([...p, ...ids])]
    )
  }

  const parsed = bands.filter((b) => b.lo !== null)
  const height = students.length * ROW + TOP + BOTTOM

  const option = useMemo(() => {
    const byId = new Map(students.map((s) => [s.id, s]))
    return {
      textStyle: baseTextStyle,
      animationDuration: 300,
      tooltip: {
        ...tooltipBase,
        trigger: 'item',
        formatter: (p) => {
          const s = byId.get(p.value[1])
          return `<b>${s.name}</b><br/>Score ${s.score} · Band ${s.bandKey}<br/>Stanine ${s.stanine === null ? 'N/A' : s.stanine}<br/>${fmtDay(s.test_date)}`
        }
      },
      grid: { top: TOP, bottom: BOTTOM, left: 118, right: 16 },
      xAxis: {
        type: 'value',
        ...(parsed.length ? { min: parsed[0].lo, max: parsed[parsed.length - 1].hi + 1 } : { scale: true }),
        name: 'PAT scale score',
        nameLocation: 'middle',
        nameGap: 22,
        nameTextStyle: { color: INK.secondary, fontFamily: FONT, fontSize: 11 },
        splitLine: { show: false },
        axisLine: { lineStyle: { color: INK.axis } },
        axisLabel: { color: INK.muted, fontSize: 10, hideOverlap: true }
      },
      yAxis: {
        type: 'category',
        inverse: true, // best result at the top, same order as the table
        data: students.map((s) => s.id),
        axisTick: { show: false },
        axisLine: { lineStyle: { color: INK.axis } },
        splitLine: { show: true, lineStyle: { color: INK.grid } },
        axisLabel: {
          interval: 0,
          color: INK.primary,
          fontSize: 11,
          fontFamily: FONT,
          width: 106,
          overflow: 'truncate',
          formatter: (id) => byId.get(id)?.name || ''
        }
      },
      series: [
        {
          type: 'scatter',
          symbolSize: 10,
          data: students.map((s) => ({
            value: [s.score, s.id],
            itemStyle: sel.has(s.id)
              ? { color: accent, borderColor: INK.primary, borderWidth: 2 }
              : { color: primary, opacity: sel.size ? 0.55 : 1 },
            symbolSize: sel.has(s.id) ? 13 : 10
          })),
          markArea: {
            silent: true,
            label: {
              show: true,
              position: 'insideTop',
              color: INK.secondary,
              fontSize: 10,
              fontFamily: FONT
            },
            data: parsed.map((b) => [
              { name: b.key, xAxis: b.lo, itemStyle: { color: b.tint } },
              { xAxis: b.hi + 1 }
            ])
          }
        }
      ]
    }
  }, [students, parsed, sel, primary, accent])

  const group = students.filter((s) => sel.has(s.id))

  return (
    <Box sx={{ height: '100%', minWidth: 0, overflow: 'auto' }}>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 0.75, mb: 0.75 }}>
        <GroupLine n={students.length} excluded={cls.excluded} noStanine={cls.noStanine} />
        {cls.yearLevels.length > 1 ? (
          <Typography sx={{ fontSize: 12, color: SUMMARY_PALETTE.bad }}>
            · {cls.yearLevels.length} year levels: stanines are read against each year level&apos;s norm.
          </Typography>
        ) : null}
      </Box>
      <Box
        sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 1 }}
        role='group'
        aria-label='Achievement bands'
      >
        {bands.map((b) => {
          const ids = students.filter((s) => s.bandKey === b.key)
          const all = ids.length > 0 && ids.every((s) => sel.has(s.id))
          return (
            <Box
              key={b.key}
              {...(ids.length ? buttonProps(() => toggleBand(b.key), all) : {})}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.75,
                px: 1,
                py: 0.25,
                borderRadius: '12px',
                fontSize: 11,
                cursor: ids.length ? 'pointer' : 'default',
                opacity: ids.length ? 1 : 0.5,
                border: `1px solid ${all ? INK.primary : SUMMARY_PALETTE.cardBorder}`,
                bgcolor: b.tint
              }}
            >
              <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: b.color }} />
              Band {b.key} · {ids.length}
            </Box>
          )
        })}
      </Box>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', columnGap: 1 }}>
        <Box ref={boxRef} sx={{ flex: '3 1 300px', minWidth: 0 }}>
          <ReactECharts
            ref={chartRef}
            option={option}
            notMerge
            style={{ height, width: '100%' }}
            onEvents={{ click: (p) => p?.value && toggle(p.value[1]) }}
          />
        </Box>
        <Box
          sx={{ flex: '2 1 260px', minWidth: 0, fontSize: 12 }}
          role='table'
          aria-label='Results by student, best first'
        >
          <Box
            role='row'
            sx={{
              display: 'flex',
              alignItems: 'center',
              height: TOP,
              color: SUMMARY_PALETTE.muted,
              fontWeight: 600,
              fontSize: 11
            }}
          >
            <Box role='columnheader' sx={{ width: 52 }}>
              Score
            </Box>
            <Box role='columnheader' sx={{ width: 60 }}>
              Stanine
            </Box>
            <Box role='columnheader' sx={{ flex: 1, minWidth: 0 }}>
              Band{showDates ? ' · Date' : ''}
            </Box>
          </Box>
          {students.map((s) => {
            const on = sel.has(s.id)
            return (
              <Box
                key={s.id}
                role='row'
                aria-label={s.name}
                {...buttonProps(() => toggle(s.id), on)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  height: ROW,
                  cursor: 'pointer',
                  borderTop: `1px solid ${INK.grid}`,
                  bgcolor: on ? '#fdebd6' : 'transparent',
                  outlineOffset: -2,
                  color: SUMMARY_PALETTE.ink
                }}
              >
                <Box role='cell' sx={{ width: 52, fontWeight: 600 }}>
                  {s.score}
                </Box>
                <Box role='cell' sx={{ width: 60 }}>
                  {s.stanine === null ? 'N/A' : s.stanine}
                </Box>
                <Box
                  role='cell'
                  sx={{
                    flex: 1,
                    minWidth: 0,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis'
                  }}
                >
                  {s.bandKey}
                  {showDates ? ` · ${fmtDay(s.test_date)}` : ''}
                </Box>
              </Box>
            )
          })}
        </Box>
      </Box>

      {!showDates && cls.dates[0] ? (
        <Typography sx={{ fontSize: 11, color: SUMMARY_PALETTE.muted }}>
          Tested {fmtDay(cls.dates[0])}
        </Typography>
      ) : null}
      {cls.noStanine > 0 ? <StanineNote /> : null}
      <Roster students={group} label='Teaching group' onClear={() => setPicked([])} />
      <Interpretation />
    </Box>
  )
}

export default PatClassRankingCard
